import {tokens} from '@pleo-io/telescope'
import {VisuallyHidden} from '@reach/visually-hidden'
import React from 'react'
import styled from 'styled-components'

import checkIcon from '@helios/shared/images/icons/check-pink.svg'
import {text} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'
import mq from '@helios/shared/utils/media-query'

interface Props {
    headColumns: string[]
    bodyHeads: string[]
    bodyColumns: Array<string | boolean>[]
    tickAltText: string
}

export const Table = ({headColumns, bodyHeads, bodyColumns, tickAltText}: Props) => (
    <TableWrapper>
        <StyledTable>
            <tr>
                {headColumns.map((headColumn, index) => (
                    <HeadColumn scope="col" key={`head-${index}`}>
                        {headColumn}
                    </HeadColumn>
                ))}
            </tr>

            {bodyColumns.map((bodyColumn, rowIndex) => (
                <BodyRow key={`body-row-${rowIndex}`}>
                    <BodyHead scope="row">{bodyHeads[rowIndex]}</BodyHead>
                    {bodyColumn.map((cell, index) => (
                        <BodyColumn key={`body-cell-${rowIndex}-${index}`}>
                            {typeof cell === 'string' && cell}
                            {cell === true && (
                                <>
                                    <StyledTick />
                                    <VisuallyHidden>{tickAltText}</VisuallyHidden>
                                </>
                            )}
                        </BodyColumn>
                    ))}
                </BodyRow>
            ))}
        </StyledTable>
    </TableWrapper>
)

const TableWrapper = styled.div`
    padding: ${tokens.spacing20} ${tokens.spacing16};
    overflow-x: auto;
    background: ${colors.background.default};

    ${mq.min('sm')} {
        padding: ${tokens.spacing40};
    }
`

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th:first-child {
        text-align: left;
    }

    td {
        text-align: center;
    }
`

const BodyHead = styled.th`
    ${text({variant: 'paragraph-base'})}
`

const BodyColumn = styled.td`
    ${text({variant: 'label-base'})}
`

const BodyRow = styled.tr`
    th,
    td {
        padding: ${tokens.spacing16} 0;
        font-weight: normal;
    }

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.border.default};
    }

    &:last-child {
        th,
        td {
            padding-bottom: 0;
        }
    }
`

const HeadColumn = styled.th`
    min-width: 120px;
    padding-bottom: 20px;
    vertical-align: top;
    ${text({variant: 'label-base'})}

    &:not(:first-child) {
        width: 20%;
        min-width: 120px;
    }

    ${mq.min('sm')} {
        font-weight: normal;
        ${text({variant: 'paragraph-large'})}
    }
`

const StyledTick = styled.span`
    display: inline-block;
    width: 16px;
    aspect-ratio: 4 / 3;
    background-image: url(${checkIcon});
    background-size: contain;

    ${mq.min('sm')} {
        width: 20px;
    }
`
